/**
 * Workout Clothing Recommendation Logic
 * Determines what clothing to wear based on weather conditions
 */

import type { ClothingItem, ClothingSet } from '../../../shared/types';
import { findMatchingClothingSets } from '../services/clothingSetsService';
import type { WeatherData } from '../services/weatherService';
import { interpretWeatherCode } from '../services/weatherService';
import { logger } from './logger';

export type { ClothingItem };

export interface ClothingRecommendation {
  top: ClothingItem[];
  bottom: ClothingItem[];
  accessories: ClothingItem[];
  recommendation: string;
  isCustomSet?: boolean;
  customSetName?: string;
  customSetId?: string;
  /** All matching custom sets (for multiple match UI) */
  allMatches?: ClothingSet[];
}

/**
 * Determines what clothing to wear based on temperature and weather conditions
 * First checks for user's custom sets, falls back to default algorithm
 * Returns all matching sets so the UI can display options for cleanup
 */
export async function getClothingRecommendation(
  weather: WeatherData
): Promise<ClothingRecommendation> {
  const { apparentTemperature, weatherCode, windSpeed } = weather;
  const condition = interpretWeatherCode(weatherCode);

  // Try to find matching custom sets (use apparent/feels-like temperature)
  try {
    const result = await findMatchingClothingSets(apparentTemperature, {
      rain: condition.isRaining,
      wind: windSpeed > 15,
      sun:
        condition.description.toLowerCase().includes('clear') ||
        condition.description.toLowerCase().includes('sunny'),
    });

    if (result.matches.length > 0) {
      // Use the first match as the primary recommendation
      const primaryMatch = result.matches[0];
      const hasMultipleMatches = result.matches.length > 1;

      return {
        top: primaryMatch.clothing.top,
        bottom: primaryMatch.clothing.bottom,
        accessories: primaryMatch.clothing.accessories,
        recommendation: hasMultipleMatches
          ? `Using "${primaryMatch.name}" (${result.matches.length} sets match)`
          : `Using your custom set: ${primaryMatch.name}`,
        isCustomSet: true,
        customSetName: primaryMatch.name,
        customSetId: primaryMatch._id,
        allMatches: result.matches,
      };
    }
  } catch (error) {
    logger.warn('Failed to fetch custom clothing set, using default', { error });
  }

  // Fall back to default algorithm
  return getDefaultClothingRecommendation(weather);
}

/**
 * Synchronous version of getClothingRecommendation for legacy support
 * Uses only the default algorithm
 */
export function getClothingRecommendationSync(weather: WeatherData): ClothingRecommendation {
  return getDefaultClothingRecommendation(weather);
}

/**
 * Default clothing recommendation algorithm
 */
function getDefaultClothingRecommendation(weather: WeatherData): ClothingRecommendation {
  const { apparentTemperature, weatherCode } = weather;
  const condition = interpretWeatherCode(weatherCode);

  // Use apparent temperature (feels-like) which already factors in wind chill and humidity
  const feelsLike = apparentTemperature;

  let top: ClothingItem;
  let bottom: ClothingItem;
  const accessories: ClothingItem[] = [];

  // Determine top clothing
  if (feelsLike < 0) {
    top = 'sweater';
  } else if (feelsLike < 10) {
    top = 't-shirt-long';
  } else if (feelsLike < 18) {
    top = 't-shirt-long';
  } else {
    top = 't-shirt-short';
  }

  // Determine bottom clothing
  if (feelsLike < 10) {
    bottom = 'long-tights';
  } else {
    bottom = 'shorts';
  }

  // Add accessories
  if (condition.isRaining || condition.isSnowing) {
    accessories.push('running-cap');
  }
  if (feelsLike < 5) {
    accessories.push('sweatband');
  }

  // Generate recommendation text
  let recommendation = '';
  if (feelsLike < 0) {
    recommendation = `Very cold! Bundle up with a running sweater and long tights.`;
  } else if (feelsLike < 10) {
    recommendation = `Cold weather. Wear long sleeves and tights.`;
  } else if (feelsLike < 18) {
    recommendation = `Cool conditions. Long sleeves recommended.`;
  } else if (feelsLike < 25) {
    recommendation = `Perfect running weather! Short sleeves and your choice of shorts or tights.`;
  } else {
    recommendation = `Warm conditions. Wear light clothing and stay hydrated!`;
  }

  if (condition.isRaining) {
    recommendation += ' Expect rain - consider a cap to keep dry.';
  } else if (condition.isSnowing) {
    recommendation += ' Snow expected - dress warmly and watch your footing.';
  }

  return {
    top: [top],
    bottom: [bottom],
    accessories,
    recommendation,
  };
}

/**
 * Gets a human-readable description of the clothing
 */
export function getClothingDescription(item: ClothingItem): string {
  switch (item) {
    case 'running-cap':
      return 'Running Cap';
    case 'sweatband':
      return 'Sweatband';
    case 'gloves':
      return 'Gloves';
    case 'buff':
      return 'Buff';
    case 't-shirt-short':
      return 'Short Sleeve T-Shirt';
    case 't-shirt-long':
      return 'Long Sleeve T-Shirt';
    case 'sweater':
      return 'Running Sweater';
    case 'shorts':
      return 'Shorts';
    case 'long-tights':
      return 'Long Tights';
    default:
      return '';
  }
}

/**
 * Get icon path for clothing item
 * Icons from Flaticon.com - Free for personal use
 */
export function getClothingIcon(item: ClothingItem): string {
  switch (item) {
    case 't-shirt-short':
      return '/icons/running-shirt.svg';
    case 't-shirt-long':
      return '/icons/running-jacket.svg';
    case 'sweater':
      return '/icons/running-jacket.svg';
    case 'shorts':
      return '/icons/running-shorts.svg';
    case 'long-tights':
      return '/icons/running-tights.svg';
    case 'running-cap':
      return '/icons/running-cap.svg';
    case 'sweatband':
      return '/icons/running-cap.svg';
    default:
      return '/icons/running-shirt.svg';
  }
}
